import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DialerService } from 'dialer';
import { forkJoin, Observable, race, timer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { RoleMgr, ShowRequest } from '../../core';
import { AppState } from '../../core/app-state';
import { AppTab } from '../../core/app-tab';
import { PathTestDoc } from '../../core/schema/path-test-schema';
import { ScoutTaskDoc } from '../../core/schema/scout-task-schema';
import { DialerClientService, durationToText, PathTestLogService, PointTestLogService, ScoutPointPhotoService, ThemeService } from '../../core/services';
import { FesBaseComponent } from '../../shared/components/fes-base.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { PointService, BuildingService, PathViewService, PathWaypointService } from '../../core/services';
import { ConfirmModalComponent } from '../../shared/components/confirm-modal/confirm-modal.component';
import { StageDoc } from '../../core/schema/stage-schema';
import { StageService } from '../../core/services/stage.service';
import { Stage } from '../../core/models/stage';
import { TableViewQueryCriteria } from '../../shared/components/table-view/table-view-component-base';
import {OneSourceExportService} from "../../core/services/onesource-export.service";
import { AppConfig } from '../../../environments/environment';
import { TestbedRxdbService } from '../../core/services/testbed-rxdb';

class AppPreferences {
  stage?: string
  isDarkTheme?: boolean
}

@Component({
  selector: 'app-home-tab',
  templateUrl: './home-tab.component.html',
  styleUrls: ['./home-tab.component.scss']
})
export class HomeTabComponent extends FesBaseComponent implements OnInit, OnDestroy {

  @Input('detailFn') public detailFn : (vi: ShowRequest) => void;

  @Input() set tabConfig( tabConfig : AppTab ) {
    this.bTabSelected = tabConfig.active
    tabConfig.setSelected = this.onTabSelected
  }

  appConfig = AppConfig;

  CorrelationDefaultContext = null //'{"filter":{"floornum":"1","buildingid":"ATLDBC27"},"visible":["market","buildingid","floornum"]}'

  dialerTimeInfo$ : Observable<any>
  dialerUptimeInfo$ : Observable<any>
  stages$: Observable<StageDoc[]>
  errorMsg: string

  showBaro = false;

  preferences: AppPreferences = {
    stage: null,
    isDarkTheme: false
  }

  constructor(
    appState: AppState,
    public roleMgr: RoleMgr,
    public dialerService: DialerService,
    public dialerClientService: DialerClientService,
    public httpClient: HttpClient,
    public pointService: PointService,
    public buildingService: BuildingService,
    public pathService: PathViewService,
    public waypointService: PathWaypointService,
    public stageService: StageService,
    private modalService: BsModalService,
    private oneSourceExportService:OneSourceExportService,
    public pointTestLogService:PointTestLogService,
    public pathTestLogService: PathTestLogService,
    protected photoService: ScoutPointPhotoService,
    protected pathWaypointService: PathWaypointService,
    private testbedRxdbService : TestbedRxdbService,
    public themeService: ThemeService,
    ) {
    super(appState)
  }

  ngOnInit(): void {
    this.initDialerTimeInfo()
    this.initDialerUptimeInfo()
    this.stages$ =  this.stageService.rxCollection.find().$.pipe( map(stages=>[null, ...stages]))
    this.preferences.stage = localStorage.getItem('preferences.stage')
    this.preferences.isDarkTheme = this.themeService.getCurrentTheme()
    
    // Subscribe to theme changes
    this.themeService.isDarkTheme$.subscribe(isDarkTheme => {
      this.preferences.isDarkTheme = isDarkTheme
    })
  }

  ngOnDestroy(): void {
    this.destroy()
  }

  initDialerTimeInfo() {
    console.log('initDialerTimeInfo()')
    const timeStatus$ = this.dialerClientService.createTimeStatusObservable(4000)
    this.dialerTimeInfo$ = timer(0,5000).pipe( switchMap( () => timeStatus$ ) )
  }



  initDialerUptimeInfo() {
    console.log('initDialerUpimeInfo()')
    this.dialerUptimeInfo$ = timer(0,15000).pipe(
      switchMap( _ => race(
        this.httpClient.get( this.dialerService.config.url + 'app/status'),
        timer(4000).pipe(map(_=>({durationMessage:'Timed out'})))
      )),
      map( (statusInfo : any) => ({
          ...statusInfo,
          durationMessage: durationToText(statusInfo.uptimeDurationSecs)
        })
      ),
    )
  }

  bTabSelected = false
  onTabSelected = ( bSelected : boolean ) => {
    this.bTabSelected = bSelected
    console.log('home tab selected: ' + bSelected )
    if ( bSelected ) {
      //this.initialize()
    } else {
      this.unsubscribe()
    }
  }


  showView( viewName: string, itemId: any = null, task: ScoutTaskDoc | PathTestDoc = null ) {
    this.detailFn( new ShowRequest('tab',viewName, itemId, task) )
  }

  showViewWithStage( viewName: string ) {
    const queryCriteria: TableViewQueryCriteria = {
      filters: this.appState.activeStage ? [{key:'stage',mode:'=',value:this.appState.activeStage}] : []
    }
    this.showView(viewName, JSON.stringify(queryCriteria))
  }

  showTaskList( filter : any = {}) {
    if ( this.preferences.stage ) {
      filter.stage = {
        value: this.preferences.stage,
        mode: '='
      }
    }
    const strFilter = JSON.stringify({filter})
    this.detailFn( new ShowRequest('tab','task list', strFilter) )
  }

  showVolunteerTasks() {
    this.detailFn( new ShowRequest('tab','volunteer tasks', null))
  }

  async enableDialer(bEnable:boolean){
    console.log(`enableDialer(bEnable=${bEnable})`)
    this.dialerClientService.enableDialer(bEnable)
    /*if ( bEnable ) {
      const status = await from(this.dialerClientService.networkStatus$.pipe(filter(s=>s!=lastStatus),first())).toPromise()
      console.log(status.eventType,status.ok)
      if ( status?.error ) {
        console.error(status.error)
        this.appState.notifyError('Failed initializing dialer. Check browser console log for details.',this,7)
      }
    }*/
  }

  async clearOneSourceCollections() {
    let initialState = {
      onYes: async () => {
        await this.pointService.rxCollection.remove().then(async () => {
          console.log('point rxCollection remove()')
          await this.buildingService.rxCollection.remove().then(async () => {
            console.log('building rxCollection remove()')
            await this.pathService.rxCollection.remove().then(async () => {
              console.log('path rxCollection remove()')
              await this.waypointService.rxCollection.remove().then(async () => {
                console.log('waypoint rxCollection remove()')
              })
            })
          })
        }).catch(error => {
          console.log('clearOneSourceCollections() Error: ', error)
        }).finally(() => {
          location.reload()
        })
      },
      title: "RxDB Collections Rebuild",
      question: `Do you wish to rebuild RxDB One Source Collections?  `
        + `<div class="m-2 small font-italic text-warning"><i class='text-warning fa fa-info-circle mr-1'></i>It may take a few minutes to re-sync Data</div>`
        + `<div>Rebuild now?</div>`,
      focusOnNo: true,
      yesStyle: 'btn btn-danger'
    }
    this.modalService.show(ConfirmModalComponent, {ignoreBackdropClick: true,initialState} );
  }

  onStageChanged( stageName: string ) {
    if ( stageName ) {
      localStorage.setItem('preferences.stage', stageName)
    } else {
      localStorage.removeItem('preferences.stage')
    }
    this.appState.activeStage = stageName
    if (stageName) {
      Stage.DefaultStageName = stageName
    }
  }

  toggleDarkTheme() {
    this.themeService.toggleTheme();
    this.preferences.isDarkTheme = this.themeService.getCurrentTheme();
  }

  bPrimingRxdbIndices = false
  async primeRxdbIndices() {
    this.bPrimingRxdbIndices = true
    try {
      console.log('priming indices...')
      await this.testbedRxdbService.primeIndices();
      console.log('Primed rxdb indices (ready).')
    } catch ( error : any ) {
      this.appState.publishErrorObj(error,"Failed priming rxdb indices", this)
    } finally {
      this.bPrimingRxdbIndices = false
    }
  }

  exportOneSource() {
    this.oneSourceExportService.exportOneSource();
  }
}
